import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Box,
  Divider,
  Chip,
  TablePagination,
  Paper,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  getLabourRates,
  LabourCostRateUserOverride,
  deleteLabourCostRateUserOverride,
} from 'api/labourApi';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import ConfirmDeleteDialog from 'components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import { User } from 'api/userApi';
import { useSnackbar } from 'notistack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  RATE_CARD_WORKING_AWAY_HEADER_TOOLTIP,
  WORKING_AWAY_INFO_ARIA_LABEL,
  WORKING_AWAY_TOOLTIP_TYPOGRAPHY_SX,
} from 'constants/workingAwayFromHomeCopy';

interface LabourCostOverrideDetailsViewProps {
  handleClose: () => void;
  selectedUser?: User;
  open: boolean;
  onEditCostRateOverride?: (rate: LabourCostRateUserOverride) => void;
}

export default function LabourCostOverrideDetailsView({
  handleClose,
  selectedUser,
  open,
  onEditCostRateOverride,
}: LabourCostOverrideDetailsViewProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<LabourCostRateUserOverride | null>(null);

  const queryClient = useQueryClient();
  const { data: labourData } = useQuery(['labour-rates'], getLabourRates);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteMutation = useMutation(deleteLabourCostRateUserOverride, {
    onSuccess: () => {
      queryClient.invalidateQueries(['labour-rates']);
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      enqueueSnackbar('Labour cost rate deleted successfully', {
        variant: 'success',
      });
    },
    onError: (error) => {
      console.error('Error deleting cost rate user override:', error);
      enqueueSnackbar('Failed to delete labour cost rate', {
        variant: 'error',
      });
    },
  });

  const displayRates = labourData
    ? labourData.costRateUserOverrides.filter(
        (override) => override.user?.id === selectedUser?.id
      )
    : [];

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            my: 1,
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <Box display="flex" alignItems="center">
            <Typography variant="h6">
              Labour Cost Rate History of{' '}
              {selectedUser
                ? `${selectedUser.firstName} ${selectedUser.lastName}`
                : 'Selected User'}
            </Typography>
            {selectedUser?.role && (
              <Chip
                label={selectedUser.role}
                size="small"
                sx={{ ml: 2, backgroundColor: 'primary.main', color: 'white' }}
              />
            )}
          </Box>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={{
            mb: 2,
          }}>
          <Box>
            <Paper elevation={0}>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell>Rate</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}>
                        Working Away From Home Rate
                        <Tooltip
                          arrow
                          title={
                            <Typography
                              component="div"
                              variant="body2"
                              sx={{
                                ...WORKING_AWAY_TOOLTIP_TYPOGRAPHY_SX,
                                maxWidth: 280,
                              }}>
                              {RATE_CARD_WORKING_AWAY_HEADER_TOOLTIP}
                            </Typography>
                          }>
                          <IconButton
                            size="small"
                            aria-label={WORKING_AWAY_INFO_ARIA_LABEL}
                            sx={{ p: 0.25 }}>
                            <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>Effective From</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayRates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography align="center" variant="body2">
                          No rate history available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {displayRates
                        .sort(
                          (a, b) =>
                            new Date(b.effectiveFrom).getTime() -
                            new Date(a.effectiveFrom).getTime()
                        )
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((rate) => (
                          <TableRow key={rate.id}>
                            <TableCell>
                              ${Number((rate as any).rate).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              $
                              {Number(
                                (rate as any).workingAwayFromHomeRate
                              ).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              {format(
                                new Date(rate.effectiveFrom),
                                'dd MMM yyyy'
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {onEditCostRateOverride && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditCostRateOverride(
                                      rate as LabourCostRateUserOverride
                                    );
                                  }}
                                  aria-label="Edit cost rate">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                sx={{ ml: 1 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedItem(rate);
                                  setDeleteDialogOpen(true);
                                }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={displayRates.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        title="Delete Confirmation"
        content={
          <>
            Are you sure you want to delete the item of{' '}
            <b>
              {selectedItem?.user
                ? `${selectedItem.user.firstName} ${selectedItem.user.lastName}`
                : 'this user'}
            </b>
            ?
            <Alert severity="warning" style={{ marginTop: '1rem' }}>
              This action is irreversible and will permanently delete the data.
            </Alert>
          </>
        }
        deleteFunc={async () => {
          await deleteMutation.mutate(selectedItem?.id);
        }}
        handleReject={() => {
          setDeleteDialogOpen(false);
          setSelectedItem(null);
        }}
        handleClose={() => {
          setDeleteDialogOpen(false);
          setSelectedItem(null);
        }}
      />
    </>
  );
}
